import { useEffect, useRef } from 'react';
import { useSumator } from '../contexts/SumatorContext';

/**
 * Keeps the Sumator widget out of the way while a page needs the space, without ever overriding
 * what happened to it afterwards.
 *
 * The page collapses the widget when it mounts. When it unmounts it puts the widget back the way
 * it found it - but only if its own collapse is still what the widget is showing. Once anything
 * else has moved the widget (a new line expands it; the operator may then collapse or expand it
 * by hand), the page's leave must leave it alone.
 *
 * Before this, both Add Contract and Payment restored with an unconditional `setCollapsed(false)`
 * on unmount. After a successful contract the page navigates away 1.5 s later, so an operator who
 * collapsed the freshly expanded widget inside that window saw it spring open again on the
 * Contracts page - which the full E2E suite caught on 2026-09-16 as an intermittent failure of
 * "adds and expands the widget only after creating a contract".
 */
export function useSumatorPageCollapse(): void {
    const { collapsed, setCollapsed } = useSumator();

    /** What the widget showed before this page collapsed it. */
    const before = useRef(collapsed);
    /** True from the page's own collapse until the widget's state changes for any other reason. */
    const ownCollapse = useRef(false);
    /** Whether the page's collapse has been observed rendered, so a later expansion is someone else's. */
    const observedCollapsed = useRef(false);

    useEffect(() => {
        before.current = collapsed;
        ownCollapse.current = true;
        observedCollapsed.current = false;
        setCollapsed(true);

        return () => {
            if (ownCollapse.current) {
                setCollapsed(before.current);
            }
        };
        // Mount and unmount only: `collapsed` is deliberately read once, at mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCollapsed]);

    useEffect(() => {
        if (!ownCollapse.current) {
            return;
        }

        if (collapsed) {
            observedCollapsed.current = true;
        } else if (observedCollapsed.current) {
            // Expanded after the page collapsed it: a new line or the operator. From here on the
            // widget's state is theirs, whatever they do with it next.
            ownCollapse.current = false;
        }
    }, [collapsed]);
}
